/**
 * Shared layout / surface tokens for Account details (fiat + blockchain).
 * Keep single-method, multi-method chooser/detail, and crypto select/detail
 * on the same spacing rhythm so switching currency doesn't jump visually.
 */
export const depositDetailsStyles = {
  /** Root of every DepositDetailsInfo body (no horizontal pad — drawer owns it). */
  root: "w-full space-y-4",

  /**
   * Loading / empty placeholder. Tall enough to approximate a typical
   * single-account detail body so the dialog doesn't flash short then grow —
   * without forcing that height onto loaded content.
   */
  loading:
    "w-full min-h-[22rem] flex items-center justify-center text-muted-foreground",

  /** Section heading above a chooser list ("How will the money be sent?", "Select network"). */
  sectionTitle: "text-sm md:text-base font-semibold text-gray-900",

  /**
   * "Accepted via" above rail pills — darker / heavier than body text so the
   * section reads clearly on chooser cards and the detail screen.
   */
  acceptedViaLabel: "text-xs font-bold uppercase tracking-wide text-gray-900",

  /** Soft info banner (multi-account explainer). */
  infoCallout:
    "flex items-center gap-1.5 px-3 py-2 rounded-lg border border-line bg-brand-primary/[0.05]",

  /** Strong warning banner (wrong network / pooled reference emphasis). */
  warningCallout: "px-3 py-2 rounded-lg border border-red-600 bg-red-50",

  warningText: "text-xs font-semibold text-red-700",

  /**
   * Selectable surface for fiat bank-detail sets and crypto networks.
   * Default = soft brand wash (former hover). Hover deepens the left wash
   * and adds a brand accent on the left edge.
   */
  chooserCard:
    "w-full text-left rounded-xl border border-brand-primary/20 shadow-sm " +
    "bg-gradient-to-l from-white to-brand-primary/5 " +
    "border-l-2 border-l-brand-primary/40 " +
    "hover:from-white hover:to-brand-primary/20 " +
    "hover:border-brand-primary/40 hover:border-l-brand-boundary " +
    "hover:shadow-md " +
    "transition-all px-6 py-4 flex items-center gap-3 cursor-pointer",

  /** Stack of chooser cards. */
  chooserList: "flex flex-col gap-3 w-full",

  /** Outlined pill — matches the currency selector in the drawer header. */
  backButton:
    "gap-2 rounded-full border border-brand-primary/30 bg-brand-primary/5 " +
    "hover:bg-brand-primary/20 font-medium",

  /** FiatMethodCard / detail body vertical rhythm. */
  methodBody: "space-y-4",

  fieldGrid:
    "grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-0.5 overflow-visible",

  fieldRow:
    "flex items-center justify-between p-3 rounded-lg hover:bg-brand-primary/5 transition-colors",

  fieldLabel: "text-sm md:text-base font-semibold text-primary",

  fieldValue:
    "text-sm md:text-base font-medium text-gray-900 whitespace-normal break-all mt-1",

  actions: "flex flex-wrap gap-2",

  actionButton: "min-w-[160px]",
} as const;
